import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface ChartProps {
  data: any[];
  type?: 'line' | 'area' | 'bar' | 'pie';
  height?: number;
  loading?: boolean;
  dataKey?: string;
  dataKeys?: string[];
  xAxisKey?: string;
  color?: string;
  colors?: string[];
}

const Chart: React.FC<ChartProps> = ({
  data,
  type = 'line',
  height = 300,
  loading = false,
  dataKey,
  dataKeys,
  xAxisKey = 'date',
  color = '#3b82f6',
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'],
}) => {
  if (loading) {
    return (
      <div className="w-full flex items-center justify-center" style={{ height }}>
        <div className="animate-pulse flex space-x-4 w-full">
          <div className="flex-1 space-y-6 py-1">
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-2 bg-slate-200 rounded col-span-2"></div>
                <div className="h-2 bg-slate-200 rounded col-span-1"></div>
              </div>
              <div className="h-2 bg-slate-200 rounded"></div>
              <div className="grid grid-cols-3 gap-4">
                <div className="h-2 bg-slate-200 rounded col-span-1"></div>
                <div className="h-2 bg-slate-200 rounded col-span-2"></div>
              </div>
              <div className="h-2 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="w-full flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <div className="text-slate-400 text-lg mb-2">📊</div>
          <p className="text-slate-500 text-sm">No data available</p>
        </div>
      </div>
    );
  }

  const formatTooltipValue = (value: any, name: string) => {
    if (typeof value === 'number') {
      if (name.toLowerCase().includes('amount') || name.toLowerCase().includes('revenue') || name.toLowerCase().includes('profit')) {
        return `₹${value.toLocaleString('en-IN')}`;
      }
      return value.toLocaleString('en-IN');
    }
    return value;
  };

  const formatXAxisLabel = (value: string) => {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleDateString('en-IN', { 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return value;
    }
  };

  const formatYAxisLabel = (value: number) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value}`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-slate-900 mb-2">
            {formatXAxisLabel(label)}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              <span className="font-medium">{entry.name}:</span>{' '}
              <span className="font-bold">{formatTooltipValue(entry.value, entry.name)}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (type === 'pie') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name} ${(percent! * 100).toFixed(0)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey={dataKey || 'value'}
          >
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip formatter={formatTooltipValue} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  const commonProps = {
    width: '100%' as const,
    height,
    data,
    margin: { top: 5, right: 30, left: 20, bottom: 5 },
  };

  const axisProps = {
    axisLine: false,
    tickLine: false,
    tick: { fontSize: 12, fill: '#64748b' },
  };

  return (
    <ResponsiveContainer {...commonProps}>
      {type === 'line' && (
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis 
            dataKey={xAxisKey} 
            {...axisProps}
            tickFormatter={formatXAxisLabel}
          />
          <YAxis 
            {...axisProps}
            tickFormatter={formatYAxisLabel}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          {dataKeys ? (
            dataKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ fill: colors[index % colors.length], strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, strokeWidth: 0 }}
              />
            ))
          ) : (
            <Line
              type="monotone"
              dataKey={dataKey || 'value'}
              stroke={color}
              strokeWidth={2}
              dot={{ fill: color, strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
          )}
        </LineChart>
      )}

      {type === 'area' && (
        <AreaChart {...commonProps}>
          <defs>
            <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.8} />
              <stop offset="95%" stopColor={color} stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis 
            dataKey={xAxisKey} 
            {...axisProps}
            tickFormatter={formatXAxisLabel}
          />
          <YAxis 
            {...axisProps}
            tickFormatter={formatYAxisLabel}
          />
          <Tooltip content={<CustomTooltip />} />
          
          <Area
            type="monotone"
            dataKey={dataKey || 'value'}
            stroke={color}
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorGradient)"
          />
        </AreaChart>
      )}

      {type === 'bar' && (
        <BarChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis 
            dataKey={xAxisKey} 
            {...axisProps}
            tickFormatter={formatXAxisLabel}
          />
          <YAxis 
            {...axisProps}
            tickFormatter={formatYAxisLabel}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          {dataKeys ? (
            dataKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={colors[index % colors.length]}
                radius={[2, 2, 0, 0]}
              />
            ))
          ) : (
            <Bar
              dataKey={dataKey || 'value'}
              fill={color}
              radius={[2, 2, 0, 0]}
            />
          )}
        </BarChart>
      )}
    </ResponsiveContainer>
  );
};

export default Chart;